import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContextType, AuthState, LoginCredentials } from '../types';
import { API_URL } from '../constants/appConstants';

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configure axios to send credentials with all requests
axios.defaults.withCredentials = true;

// Session heartbeat interval (15 minutes)
const SESSION_HEARTBEAT_INTERVAL = 15 * 60 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/check`);
      
      if (response.data.authenticated) {
        setState({
          isAuthenticated: true,
          user: response.data.user,
          loading: false,
          error: null,
        });
      } else {
        setState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
    }
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await axios.post(`${API_URL}/auth/login`, credentials);

      if (response.data.success) {
        setState({
          isAuthenticated: true,
          user: response.data.user,
          loading: false,
          error: null,
        });
        return true;
      } else {
        setState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: response.data.error || 'Login failed',
        });
        return false;
      }
    } catch (error: any) {
      const errorMessage = 
        error.response?.data?.error || 
        error.message || 
        'An error occurred during login';
      
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: errorMessage,
      });
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      });
    }
  }, []);

  // Intentionally no global 401 -> auto-logout interceptor.
  // The backend silently renews stale intranet sessions in callIntranet using
  // the user's stored encrypted credentials, so a transient intranet 401 should
  // not eject the user from the app.

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Session heartbeat - keeps intranet session alive while app is open
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }

    // Only run heartbeat when authenticated
    if (!state.isAuthenticated) {
      return;
    }

    const refreshSession = async () => {
      try {
        const response = await axios.post(`${API_URL}/auth/refresh`);
        
        // If intranet session expired, force re-login
        if (response.data.requiresRelogin) {
          console.warn('[Session] Intranet session expired - logging out');
          logout();
        }
      } catch (error: any) {
        // 401 means session expired
        if (error.response?.status === 401) {
          console.warn('[Session] Session expired - logging out');
          logout();
        }
      }
    };

    // Start heartbeat - delay first check by 5 minutes to avoid immediate logout
    // after fresh login (gives intranet session time to stabilize)
    const initialDelayTimer = setTimeout(() => {
      heartbeatRef.current = setInterval(refreshSession, SESSION_HEARTBEAT_INTERVAL);
    }, 5 * 60 * 1000); // Wait 5 minutes before starting heartbeat

    // Cleanup on unmount or when auth state changes
    return () => {
      clearTimeout(initialDelayTimer);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [state.isAuthenticated, logout]);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

